import type { PageApi } from '@shared/api/pages/types'
import type { Artifact } from '@shared/types/artifacts'
import type { AxiosInstance } from 'axios'
import { getApiRoutes, resolveRoute } from '@shared/api/routes'
import { API_WEB_COMPONENTS } from '@shared/api/web-components/deprecated'
import { hostLogger } from '@shared/logging/host-logger'
import { ArtifactStatus, ArtifactType } from '@shared/types/artifacts'
import { isWippyPackageESMModule, isWippyPackageWebPage } from '@shared/wippy-package/wippy-package'

export interface Endpoints {
  meta: (uuid: string) => string
  content: (uuid: string) => string
  baseUrl: (uuid: string) => string
}

export type Content = {
  data: string
  contentType: 'text/markdown' | 'text/html'
} | {
  data: object
  contentType: 'application/json'
}

export const defaultArtifactEndpoint: Endpoints = {
  meta: (uuid: string) => resolveRoute(getApiRoutes().artifacts.get, { uuid }),
  content: (uuid: string) => resolveRoute(getApiRoutes().artifacts.content, { uuid }),
  baseUrl: (uuid: string) => resolveRoute(getApiRoutes().artifacts.baseUrl, { uuid }),
}

export const defaultPageEndpoints: Endpoints = {
  meta: (uuid: string) => resolveRoute(getApiRoutes().pages.get, { pageId: uuid }),
  content: (uuid: string) => resolveRoute(getApiRoutes().pages.content, { pageId: uuid }),
  baseUrl: (uuid: string) => resolveRoute(getApiRoutes().pages.baseUrl, { pageId: uuid }),
}

export function makeArtifactApi<T extends Artifact | PageApi.Page>(axiosInstance: AxiosInstance, endpoints: Endpoints = defaultArtifactEndpoint) {
  /**
   * @deprecated
   */
  const getWebComponentBaseUrl = async (name: string): Promise<string> => {
    return Promise.resolve(`${API_WEB_COMPONENTS}/${name}`)
  }
  /**
   * @deprecated
   * Remove this after EE2-1845 is done
   */
  const getWebComponentJson = async (name: string) => {
    return (await axiosInstance.get<object>(`${API_WEB_COMPONENTS}/${name}/package.json`)).data
  }
  const getMeta = async (uuid: string): Promise<T> => {
    try {
      const response = await axiosInstance.get<T>(endpoints.meta(uuid))
      return response.data
    }
    catch (e) {
      // TODO: remove after EE2-1845, just throw error instead
      hostLogger.warn('Artifact meta API failed, falling back to component API', { uuid, error: e })
      const fakeArt = await getWebComponentJson(uuid)
      if (isWippyPackageESMModule(fakeArt) || isWippyPackageWebPage(fakeArt)) {
        const art: Artifact = {
          uuid,
          title: fakeArt.title || fakeArt.name || 'Artifact',
          icon: 'tabler:package',
          content_type: 'application/json',
          status: ArtifactStatus.RUNNING,
          type: ArtifactType.INLINE,
        }
        return art as T
      }
      else {
        throw e
      }
    }
  }

  const getContent = async (uuid: string): Promise<Content> => {
    try {
      const response = await axiosInstance.get<string | object>(endpoints.content(uuid))
      const data = response.data
      if (typeof data === 'string') {
        const fallbackContentType = 'text/html'
        return {
          data,
          contentType: (response.headers['content-type'] as 'text/html' | 'text/markdown') ?? fallbackContentType,
        }
      }
      else {
        const fallbackContentType = 'application/json'
        return {
          data,
          contentType: (response.headers['content-type'] as 'application/json') ?? fallbackContentType,
        }
      }
    }
    catch (e) {
      hostLogger.warn('Artifact content API failed, falling back to component API', { uuid, error: e })

      const content = (await getWebComponentJson(uuid))
      return { data: content, contentType: 'application/json' }
    }
  }

  const getBaseUrl = async (uuid: string): Promise<string> => {
    try {
      return (await axiosInstance.get<{ baseUrl: string }>(endpoints.baseUrl(uuid))).data.baseUrl
    }
    catch (e) {
      hostLogger.warn('Artifact baseUrl API failed, falling back to component API', { uuid, error: e })

      return getWebComponentBaseUrl(uuid)
    }
  }

  const getBaseContent = async (uuid: string, entryPath: string): Promise<{ baseUrl: string, content: string }> => {
    const { baseUrl, content } = (await axiosInstance.get<{ baseUrl: string, content?: string }>(endpoints.baseUrl(uuid))).data
    if (content) {
      return { content, baseUrl }
    }
    else {
      const fullHtmlFilePath = `${baseUrl}${entryPath}`

      const cont = (await axiosInstance.get(fullHtmlFilePath)).data

      return { content: cont, baseUrl }
    }
  }

  return {
    getMeta,
    getContent,
    getBaseUrl,
    getBaseContent,
  }
}
